import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { parseProjectPhase, ProjectPhase } from "../../../../../../../../../constants/project-phase";
import { ProjectDates, ProjectDatesData, ProjectDatesSchema } from "../../../../../../../../../models";
import { getProjectDetails, getStandardSpecificSchemaDetails } from "../../../../../../../../../service/query";
import { Status } from "../../../../../../../../../service/Shared";
import { jsonReviver, setValueFromFlattenedKey } from "../../../../../../../../../utils";

interface UseProjectSelectionProps {
  defaultProjectUuid?: string;
  selectedProject: ProjectDatesData | undefined;
  setSelectedProject: Dispatch<SetStateAction<ProjectDatesData | undefined>>;
}

interface UseProjectSelectionReturnData {
  dateSchema: ProjectDatesSchema | undefined;
  addedCycleCount: number;
  handleSelectProject: (value: string) => void;
  handleUpdateProjectDates: (date: Date | number | undefined, key: string) => void;
  handleAddCycle: () => void;
  handleRemoveCycle: (cycleUuid: string) => void;
}

export const useProjectSelection = ({
  defaultProjectUuid,
  selectedProject,
  setSelectedProject,
}: UseProjectSelectionProps): UseProjectSelectionReturnData => {
  // Use this to determine whether we can remove a cycle (if it was just created by user)
  const [addedCycleCount, setAddedCycleCount] = useState(0);
  const [dateSchema, setDateSchema] = useState<ProjectDatesSchema>();

  const handleSelectProject = async (value: string): Promise<void> => {
    if (value === "") return;

    const response = await getProjectDetails({ projectUuid: value, projectType: null, projectExternalReference: null });
    if (response.status === Status.Success && response.data) {
      setSelectedProject({
        uuid: response.data.uuid,
        displayName: response.data.displayName,
        currentStatus: response.data.status,
        cachedPhase: parseProjectPhase(response.data.cachedPhase) ?? ProjectPhase.feasibility,
        dates: JSON.parse(response.data.dates, jsonReviver) as ProjectDates,
        rowVersion: response.data.rowVersion,
        milestones: undefined,
        standardSpecificSchemaUuid: response.data.standardSpecificSchemaUuid,
      });
    }
  };

  const handleUpdateProjectDates = (date: Date | number | undefined, key: string): void => {
    if (selectedProject === undefined) return;

    setSelectedProject((prevProject) =>
      prevProject !== undefined
        ? { ...prevProject, dates: setValueFromFlattenedKey(key, date, prevProject.dates) }
        : undefined
    );
  };

  const handleAddCycle = (): void => {
    if (selectedProject === undefined) return;

    const cyclesLength = selectedProject.dates.ongoingMonitoring.cycles.length;
    const newCycle = {
      uuid: uuidv4(),
      sequence: cyclesLength + 1,
    };
    const updatedDates = setValueFromFlattenedKey(
      `ongoingMonitoring.cycles[${cyclesLength}]`,
      newCycle,
      selectedProject.dates
    ) as ProjectDates;
    setSelectedProject({ ...selectedProject, dates: updatedDates });
    setAddedCycleCount(cyclesLength === 0 ? 0 : addedCycleCount + 1);
  };

  const handleRemoveCycle = (cycleUuid: string): void => {
    if (selectedProject === undefined) return;

    setSelectedProject({
      ...selectedProject,
      dates: {
        ...selectedProject.dates,
        ongoingMonitoring: {
          ...selectedProject.dates.ongoingMonitoring,
          cycles: selectedProject.dates.ongoingMonitoring.cycles.filter((cycle) => cycle.uuid !== cycleUuid),
        },
      },
    });
    setAddedCycleCount(addedCycleCount - 1);
  };

  const getAndSetProjectDatesSchema = useCallback(
    async (uuid: string): Promise<void> => {
      const result = await getStandardSpecificSchemaDetails({ standardSpecificSchemaUuid: uuid });
      if (result.status === Status.Success && result.data) {
        setDateSchema(JSON.parse(result.data.dateSchema).properties as ProjectDatesSchema);
      }
    },
    [selectedProject?.standardSpecificSchemaUuid]
  );

  useEffect(() => {
    // Reset addedCycleCount when the project changes
    setAddedCycleCount(0);

    // If there are no cycles present, add one
    if (selectedProject && selectedProject.dates.ongoingMonitoring.cycles === undefined) {
      // eslint-disable-next-line no-param-reassign
      selectedProject.dates.ongoingMonitoring.cycles = [];
    }

    if (selectedProject?.dates.ongoingMonitoring.cycles.length === 0) {
      handleAddCycle();
    }

    if (selectedProject?.standardSpecificSchemaUuid)
      getAndSetProjectDatesSchema(selectedProject?.standardSpecificSchemaUuid);
  }, [selectedProject?.uuid]);

  useEffect(() => {
    // If we pass a default projectUuid into the component select it
    if (defaultProjectUuid !== undefined) {
      handleSelectProject(defaultProjectUuid);
    }
  }, [defaultProjectUuid]);

  return {
    dateSchema,
    addedCycleCount,
    handleSelectProject,
    handleUpdateProjectDates,
    handleAddCycle,
    handleRemoveCycle,
  };
};
